import { useState, useEffect, useCallback } from "react";
import { organizationApiService, apiV2Service, v2PublicApiService } from "../../../../services/apiV2Service";
import * as localService from "../../../../services/localService";
import logger from "../../../../utils/logger";
import toastUtils from "../../../../utils/toast";
import { showApiMessage } from "../../../../utils/messageHandler";

export const useOrganizationManagement = () => {
  // Organization creation states
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);

  const [createOrgForm, setCreateOrgForm] = useState({
    name: "",
    code: "",
    contactEmail: "",
    alternateEmail: "",
    contactMsisdn: "",
    address: "",
    city: "",
    region: "",
    countryId: "",
    webUrl: "",
    logoUrl: "",
    domain: "",
    spaceType: "",
    config: {
      maxUsers: "",
      enableMonthlyStatementAlerts: false,
    },
    adminProfile: {
      firstName: "",
      lastName: "",
      emailAddress: "",
      username: "",
      msisdn: "",
    },
  });

  // Edit organization states
  const [showEditOrganizationModal, setShowEditOrganizationModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [editOrganizationForm, setEditOrganizationForm] = useState({});
  const [editOrganizationLoading, setEditOrganizationLoading] = useState(false);
  const [editOrganizationError, setEditOrganizationError] = useState(null);
  const [fetchingOrganizationData, setFetchingOrganizationData] = useState(false);
  const [adminProfiles, setAdminProfiles] = useState([]);

  // Delete organization states
  const [showDeleteOrganizationModal, setShowDeleteOrganizationModal] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] = useState(null);
  const [deleteOrganizationLoading, setDeleteOrganizationLoading] = useState(false);

  // Loading states
  const [isBookingLoading, setIsBookingLoading] = useState(false);

  // Organization validation states
  const [isCheckingOrganization, setIsCheckingOrganization] = useState(false);
  const [organizationCodeAvailable, setOrganizationCodeAvailable] = useState(null);
  const [organizationNameAvailable, setOrganizationNameAvailable] = useState(null);
  const [organizationCodeError, setOrganizationCodeError] = useState("");
  const [organizationNameError, setOrganizationNameError] = useState("");

  // Username validation states
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameError, setUsernameError] = useState("");

  // Organization validation checking with debounce
  const checkOrganizationAvailability = useCallback(async (codeToCheck, nameToCheck) => {
    // At least one field must be provided
    if (!codeToCheck && !nameToCheck) {
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("");
      setOrganizationNameError("");
      return;
    }

    setIsCheckingOrganization(true);
    setOrganizationCodeError("");
    setOrganizationNameError("");

    try {
      const response = await v2PublicApiService.checkOrganizationExists({
        code: codeToCheck || undefined,
        name: nameToCheck || undefined,
      });

      const { codeExists, nameExists } = response.data;

      // Handle code validation
      if (codeToCheck) {
        if (codeExists === true) {
          setOrganizationCodeAvailable(false);
          setOrganizationCodeError("Organization code is already taken");
        } else if (codeExists === false) {
          setOrganizationCodeAvailable(true);
          setOrganizationCodeError("");
        } else {
          setOrganizationCodeAvailable(null);
          setOrganizationCodeError("");
        }
      }

      // Handle name validation
      if (nameToCheck) {
        if (nameExists === true) {
          setOrganizationNameAvailable(false);
          setOrganizationNameError("Organization name is already taken");
        } else if (nameExists === false) {
          setOrganizationNameAvailable(true);
          setOrganizationNameError("");
        } else {
          setOrganizationNameAvailable(null);
          setOrganizationNameError("");
        }
      }
    } catch (error) {
      logger.error("Organization availability check error:", error);
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("Unable to check organization availability");
      setOrganizationNameError("Unable to check organization availability");
    } finally {
      setIsCheckingOrganization(false);
    }
  }, []);

  // Username validation checking with debounce
  const checkUsernameAvailability = useCallback(async usernameToCheck => {
    if (!usernameToCheck || usernameToCheck.length < 3) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    setIsCheckingUsername(true);
    setUsernameError("");

    try {
      const response = await apiV2Service.checkUsernameAvailability(usernameToCheck);
      logger.log("Username check response:", response);
      logger.log("Response data:", response.data);
      const exists = response.data?.exists || false;
      logger.log("Exists value:", exists);

      if (exists) {
        setUsernameAvailable(false);
        setUsernameError("Username is already taken");
      } else {
        setUsernameAvailable(true);
        setUsernameError("");
      }
    } catch (error) {
      logger.error("Username availability check error:", error);
      setUsernameAvailable(null);
      setUsernameError("Username is unavailable");
    } finally {
      setIsCheckingUsername(false);
    }
  }, []);

  // Alphanumeric validation for organization code
  const isAlphanumeric = str => {
    return /^[a-zA-Z0-9]+$/.test(str);
  };

  // Debounced organization validation check
  useEffect(() => {
    if (!createOrgForm.code && !createOrgForm.name) {
      setOrganizationCodeAvailable(null);
      setOrganizationNameAvailable(null);
      setOrganizationCodeError("");
      setOrganizationNameError("");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkOrganizationAvailability(createOrgForm.code, createOrgForm.name);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [createOrgForm.code, createOrgForm.name, checkOrganizationAvailability]);

  // Debounced username validation check
  useEffect(() => {
    if (!createOrgForm.adminProfile?.username) {
      setUsernameAvailable(null);
      setUsernameError("");
      return;
    }

    const timeoutId = setTimeout(() => {
      checkUsernameAvailability(createOrgForm.adminProfile.username);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [createOrgForm.adminProfile?.username, checkUsernameAvailability]);

  // Organization creation functionality
  const handleCreateOrgInputChange = useCallback(e => {
    const { name, value, type, checked } = e.target;
    logger.log("Form input changed:", { name, value, type });

    if (name.includes(".")) {
      // Handle nested object properties (config.maxUsers, adminProfile.firstName, etc.)
      const [parent, child] = name.split(".");
      setCreateOrgForm(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : type === "number" ? parseInt(value) : value,
        },
      }));
    } else {
      setCreateOrgForm(prev => {
        const newForm = {
          ...prev,
          [name]:
            type === "checkbox"
              ? checked
              : type === "number"
                ? parseInt(value)
                : name === "countryId"
                  ? parseInt(value) // Ensure countryId is a number
                  : value,
        };

        // Auto-populate domain when webUrl changes
        if (name === "webUrl" && value) {
          try {
            const url = value.startsWith("http") ? value : `https://${value}`;
            const domain = new URL(url).hostname.replace("www.", "");
            newForm.domain = domain;
          } catch {
            // If URL is invalid, try to extract domain manually
            const domain = value.replace(/^(https?:\/\/)?(www\.)?/, "").split("/")[0];
            newForm.domain = domain;
          }
        }

        return newForm;
      });
    }
  }, []);

  const areCreateOrgFieldsOkay = useCallback(() => {
    const { name, code, contactEmail, adminProfile, countryId, spaceType, config } = createOrgForm;
    const { firstName, lastName, emailAddress, msisdn, username } = adminProfile || {};

    // Required: name, contactEmail, countryId, spaceType, maxUsers
    // Optional: code, city, region, webUrl, domain, logoUrl
    const orgFieldsValid = name && contactEmail && countryId && spaceType;

    // Check required fields for admin profile
    const adminFieldsValid = firstName && lastName && emailAddress && msisdn && username;

    // Check required config fields
    const configValid = config && config.maxUsers !== undefined && config.maxUsers !== "";

    // Check organization code validation only if provided
    if (code && !isAlphanumeric(code)) {
      setOrganizationCodeError("Organization code must contain only letters and numbers");
      return false;
    }

    // Only check availability if values are provided
    if (code && organizationCodeAvailable === false) {
      return false;
    }

    if (organizationNameAvailable === false) {
      return false;
    }

    // Check username validation
    if (username && username.length < 3) {
      setUsernameError("Username must be at least 3 characters long");
      return false;
    }

    if (usernameAvailable === false) {
      return false;
    }

    logger.log("Form validation details:");
    logger.log("Organization fields valid:", orgFieldsValid);
    logger.log("Admin fields valid:", adminFieldsValid);
    logger.log("Config valid:", configValid);

    return orgFieldsValid && adminFieldsValid && configValid;
  }, [createOrgForm, organizationCodeAvailable, organizationNameAvailable, usernameAvailable]);

  const createOrganization = useCallback(async () => {
    logger.log("Create Organization clicked");
    logger.log("Current form state:", createOrgForm);

    if (!areCreateOrgFieldsOkay()) {
      logger.log("Form validation failed");
      toastUtils.error("Please fill in all required fields");
      return;
    }

    const currentUser = localService.getUser();
    const parentOrganizationId = currentUser?.organizationId || currentUser?.organization?.id;

    logger.log("Current user:", currentUser);
    logger.log("currentUser?.organizationId:", currentUser?.organizationId);
    logger.log("currentUser?.organization?.id:", currentUser?.organization?.id);
    logger.log("Parent organization ID:", parentOrganizationId);

    if (!parentOrganizationId) {
      logger.error("No parent organization ID found!");
      toastUtils.error("Missing parent organization information. Please contact support.");
      return;
    }

    setIsBookingLoading(true);
    try {
      const organizationPayload = {
        name: createOrgForm.name,
        ...(createOrgForm.code && { code: createOrgForm.code }), // Optional
        contactEmail: createOrgForm.contactEmail,
        alternateEmail: createOrgForm.alternateEmail || "",
        contactMsisdn: createOrgForm.contactMsisdn || "",
        ...(createOrgForm.address && { address: createOrgForm.address }), // Optional
        ...(createOrgForm.city && { city: createOrgForm.city }), // Optional
        ...(createOrgForm.region && { region: createOrgForm.region }), // Optional
        countryId: createOrgForm.countryId,
        ...(createOrgForm.logoUrl && { logoUrl: createOrgForm.logoUrl }), // Optional - no default
        ...(createOrgForm.domain && { domain: createOrgForm.domain }), // Optional
        ...(createOrgForm.webUrl && { webUrl: createOrgForm.webUrl }), // Optional
        spaceType: createOrgForm.spaceType,
        config: {
          maxUsers: createOrgForm.config?.maxUsers || 0,
          enableMonthlyStatementAlerts: createOrgForm.config?.enableMonthlyStatementAlerts || false,
        },
        adminProfile: createOrgForm.adminProfile,
        confirmationUrl: `${window.location.origin}/?action=confirm_account`,
      };

      logger.log("Organization payload:", organizationPayload);
      logger.log("Making API call to create organization...");

      const response = await organizationApiService.createPropertyManagementClientOrganization(
        parentOrganizationId,
        organizationPayload
      );

      logger.log("Organization created successfully!");
      showApiMessage(response, "create client organization");
      return { success: true };
    } catch (err) {
      logger.error("Error creating organization:", err);
      logger.error("Error response:", err.response);
      logger.error("Error response data:", err.response?.data);
      logger.error("Error response status:", err.response?.status);
      logger.error("Error creating organization:", err);
      showApiMessage(err, "create client organization");
      return { success: false, error: err };
    } finally {
      setIsBookingLoading(false);
    }
  }, [createOrgForm, areCreateOrgFieldsOkay]);

  // Add employee to child organization functionality
  const addEmployeeToChildOrganization = useCallback(async (childOrganizationId, employeeData, organizationRoleId) => {
    logger.log("Adding employee to child organization:", {
      childOrganizationId,
      employeeData,
      organizationRoleId,
    });

    setIsBookingLoading(true);
    try {
      const payload = {
        organizationId: childOrganizationId,
        organizationRoleId: organizationRoleId,
        username: employeeData.username,
        firstName: employeeData.firstName,
        lastName: employeeData.lastName,
        emailAddress: employeeData.emailAddress,
        msisdn: employeeData.msisdn || "",
        confirmationUrl: `${window.location.origin}/?action=confirm_account`,
      };

      logger.log("Employee payload:", payload);
      logger.log("Making API call to add employee...");

      const response = await apiV2Service.inviteUserV2(payload);

      logger.log("Employee added successfully!");
      showApiMessage(response, "add employee");
      return { success: true };
    } catch (err) {
      logger.error("Error adding employee:", err);
      logger.error("Error response:", err.response);
      logger.error("Error response data:", err.response?.data);
      logger.error("Error response status:", err.response?.status);
      logger.error("Error adding employee:", err);
      showApiMessage(err, "add employee");
      return { success: false, error: err };
    } finally {
      setIsBookingLoading(false);
    }
  }, []);

  const populateForm = data => {
    setEditOrganizationForm({
      name: data.name || "",
      code: data.code || "",
      status: data.status || "ACTIVE",
      contactEmail: data.contactEmail || "",
      alternateEmail: data.alternateEmail || "",
      contactMsisdn: data.contactMsisdn || "",
      address: data.address || "",
      city: data.city || "",
      region: data.region || "",
      logoUrl: data.logoUrl || "",
      domain: data.domain || "",
      webUrl: data.webUrl || "",
      spaceType: data.spaceType || "LOW",
      countryId: data.countryId || 119,
      typeId: data.typeId || 3,
      maxUsers: data.config?.maxUsers || 10,
      enableMonthlyStatementAlerts: data.config?.enableMonthlyStatementAlerts || false,
    });
  };

  // Edit organization functionality
  const handleEditOrganization = useCallback(async (organization, fullOrgData = null) => {
    setSelectedOrganization(organization);
    setEditOrganizationError(null);
    setShowEditOrganizationModal(true);

    if (fullOrgData) {
      populateForm(fullOrgData);
      setAdminProfiles(fullOrgData.adminProfiles || []);
      return;
    }

    setFetchingOrganizationData(true);
    try {
      const response = await organizationApiService.getOrganizationById(organization.id);
      const organizationData = response.data.data;
      populateForm(organizationData);
      setAdminProfiles(organizationData.adminProfiles || []);
    } catch (error) {
      logger.error("Error fetching organization data:", error);
      setEditOrganizationError("Failed to load organization data. Please try again.");
      populateForm(organization);
      setAdminProfiles([]);
      showApiMessage(error, "load organization");
    } finally {
      setFetchingOrganizationData(false);
    }
  }, []);

  const handleEditOrganizationInputChange = useCallback(e => {
    const { name, value, type, checked } = e.target;
    setEditOrganizationForm(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseInt(value) || 0 : value,
    }));
  }, []);

  const handleEditOrganizationSubmit = useCallback(async () => {
    if (!editOrganizationForm.name || !editOrganizationForm.code || !editOrganizationForm.contactEmail) {
      setEditOrganizationError("Please fill in all required fields (Name, Code, Contact Email)");
      return;
    }

    if (
      editOrganizationForm.status === "ACTIVE" &&
      selectedOrganization?.status !== "ACTIVE" &&
      !adminProfiles.some(admin => admin.status === "ACTIVE")
    ) {
      toastUtils.error("Cannot activate organization. At least one admin must be active.");
      return;
    }

    setEditOrganizationLoading(true);
    setEditOrganizationError(null);

    try {
      const payload = {
        name: editOrganizationForm.name,
        code: editOrganizationForm.code,
        contactEmail: editOrganizationForm.contactEmail,
      };

      // Only include optional fields if they have values (not empty strings)
      if (editOrganizationForm.status) payload.status = editOrganizationForm.status;
      if (editOrganizationForm.alternateEmail) payload.alternateEmail = editOrganizationForm.alternateEmail;
      if (editOrganizationForm.contactMsisdn) payload.contactMsisdn = editOrganizationForm.contactMsisdn;
      if (editOrganizationForm.address) payload.address = editOrganizationForm.address;
      if (editOrganizationForm.city) payload.city = editOrganizationForm.city;
      if (editOrganizationForm.region) payload.region = editOrganizationForm.region;
      if (editOrganizationForm.logoUrl) payload.logoUrl = editOrganizationForm.logoUrl;
      if (editOrganizationForm.domain) payload.domain = editOrganizationForm.domain;
      if (editOrganizationForm.webUrl) payload.webUrl = editOrganizationForm.webUrl;

      const response = await organizationApiService.updateClientOrganization(selectedOrganization.id, payload);
      showApiMessage(response, "update organization");
      setShowEditOrganizationModal(false);
      return { success: true };
    } catch (error) {
      logger.error("Error updating organization:", error);
      setEditOrganizationError("Failed to update organization. Please try again.");
      showApiMessage(error, "update organization");
      return { success: false, error };
    } finally {
      setEditOrganizationLoading(false);
    }
  }, [editOrganizationForm, selectedOrganization, adminProfiles]);

  // Delete organization functionality
  const handleDeleteOrganization = useCallback(organization => {
    setOrganizationToDelete(organization);
    setShowDeleteOrganizationModal(true);
  }, []);

  const handleDeleteOrganizationConfirm = useCallback(async () => {
    if (!organizationToDelete) return;

    setDeleteOrganizationLoading(true);

    try {
      await organizationApiService.deleteClientOrganization(organizationToDelete.id);
      showApiMessage({ status: 200, data: { message: "Organization deleted successfully" } }, "delete organization");
      setShowDeleteOrganizationModal(false);
      setOrganizationToDelete(null);
      return { success: true };
    } catch (error) {
      logger.error("Error deleting organization:", error);
      showApiMessage(error, "delete organization");
      return { success: false, error };
    } finally {
      setDeleteOrganizationLoading(false);
    }
  }, [organizationToDelete]);

  return {
    // States
    isCreatingOrganization,
    setIsCreatingOrganization,
    createOrgForm,
    setCreateOrgForm,
    showEditOrganizationModal,
    setShowEditOrganizationModal,
    selectedOrganization,
    editOrganizationForm,
    editOrganizationLoading,
    editOrganizationError,
    fetchingOrganizationData,
    showDeleteOrganizationModal,
    setShowDeleteOrganizationModal,
    organizationToDelete,
    deleteOrganizationLoading,
    isBookingLoading,

    // Organization validation states
    isCheckingOrganization,
    organizationCodeAvailable,
    organizationNameAvailable,
    organizationCodeError,
    organizationNameError,

    // Username validation states
    isCheckingUsername,
    usernameAvailable,
    usernameError,

    // Actions
    handleCreateOrgInputChange,
    createOrganization,
    addEmployeeToChildOrganization,
    handleEditOrganization,
    handleEditOrganizationInputChange,
    handleEditOrganizationSubmit,
    handleDeleteOrganization,
    handleDeleteOrganizationConfirm,
  };
};
